import { apiRequest } from "./client";
import { readLocalImage } from "./local-images";
import { uploadAssetPath } from "@/lib/attachment-media";
import { captureDesktopChatAuthScopeGuard } from "@/desktop/desktop-chat-auth-scope";
import { syncImageAsset } from "./uploads";

export type LocalImageSync = {
  id: string;
  file: File;
  isCurrent: () => boolean;
};

/** A sync action is offered only for owned local assets present on this device. */
export async function prepareLocalImageSync(
  source: string,
): Promise<LocalImageSync | null> {
  const match = uploadAssetPath(source)?.match(
    /^\/api\/v1\/uploads\/([\da-f]{8}-[\da-f]{4}-[\da-f]{4}-[\da-f]{4}-[\da-f]{12})\/image\/$/i,
  );
  if (!match) return null;
  const id = match[1]!;
  const isCurrent = captureDesktopChatAuthScopeGuard();
  const dataUrl = await readLocalImage(id);
  if (!isCurrent()) return null;
  const access = await apiRequest<{ data: { storage_kind: string } }>(
    `/api/v1/uploads/${id}/access/`,
    { silent: true, requestGuard: isCurrent },
  );
  if (!isCurrent() || access.data.storage_kind !== "local") return null;
  const data = dataUrl.match(
    /^data:(image\/(?:png|jpeg|gif|webp|bmp));base64,([a-z\d+/=]+)$/i,
  );
  if (!data) return null;
  const bytes = Uint8Array.from(atob(data[2]!), (character) =>
    character.charCodeAt(0),
  );
  return {
    id,
    isCurrent,
    file: new File([bytes], `image-${id}.${data[1]!.split("/")[1]}`, {
      type: data[1],
    }),
  };
}

export async function syncPreparedLocalImage(
  image: LocalImageSync,
): Promise<void> {
  if (!image.isCurrent()) throw new Error("The signed-in account changed.");
  await syncImageAsset(image.id, image.file, image.isCurrent);
}
